import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import { Users, Search, X, ShoppingCart, ExternalLink } from 'lucide-react';
import { useCan, pickActiveTab, Can, NoAccess, TAB_CAP } from '../../lib/rbac';

interface Buyer {
  nickname: string;
  buyer_id: number;
  total_orders: number;
  total_spent: number;
  first_order: string;
  last_order: string;
  sites: string[];
}

interface BuyerDetail extends Buyer {
  recent_orders: {
    ml_order_id: number;
    status: string;
    paid_amount: number;
    currency_id: string;
    date_created: string;
    context_site: string;
  }[];
}

interface BuyerStats {
  total_buyers: number;
  new_buyers_30d: number;
  avg_orders_per_buyer: number;
  top_buyer: { nickname: string; total_orders: number; total_spent: number } | null;
}

const SITE_FLAGS: Record<string, string> = {
  MLA: '\u{1F1E6}\u{1F1F7}',
  MLB: '\u{1F1E7}\u{1F1F7}',
  MLM: '\u{1F1F2}\u{1F1FD}',
  MLC: '\u{1F1E8}\u{1F1F1}',
  MCO: '\u{1F1E8}\u{1F1F4}',
  MLU: '\u{1F1FA}\u{1F1FE}',
  MPE: '\u{1F1F5}\u{1F1EA}',
  MEC: '\u{1F1EA}\u{1F1E8}',
};

const STATUS_COLORS: Record<string, string> = {
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

export default function ContactsPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [activeTab, setActiveTab] = useState<'buyers' | 'suppliers'>('buyers');
  // Pestaña efectiva: la elegida si está permitida; si no, la primera permitida.
  const active = pickActiveTab(
    'contacts',
    [{ key: 'buyers' }, { key: 'suppliers' }],
    activeTab,
    can,
  ) as 'buyers' | 'suppliers' | null;
  useEffect(() => {
    if (active && active !== activeTab) setActiveTab(active);
  }, [active, activeTab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{t('contacts.title')}</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-1 w-fit">
        {(['buyers', 'suppliers'] as const).map(tab => (
          <Can key={tab} cap={TAB_CAP.contacts[tab]}>
            <button
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                active === tab
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]'
              }`}
            >
              {t(`contacts.${tab}`)}
            </button>
          </Can>
        ))}
      </div>

      {active === 'buyers' ? <BuyersTab /> : <SuppliersTab />}
    </div>
  );
}

function BuyersTab() {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [stats, setStats] = useState<BuyerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const pag = usePagination('contacts');
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const [buyersRes, statsRes] = await Promise.all([
      api.get<any>(`/contacts/buyers?page=${pag.page}&per_page=${pag.perPage}${searchParam}`),
      api.get<BuyerStats>('/contacts/buyers/stats'),
    ]);
    if (buyersRes.success && buyersRes.data) {
      setBuyers(buyersRes.data);
      const meta = (buyersRes as any).meta;
      if (meta) pag.setTotal(meta.total || 0);
    }
    if (statsRes.success && statsRes.data) setStats(statsRes.data);
    setLoading(false);
  }, [pag.page, pag.perPage, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (val: string) => {
    setSearch(val);
    pag.resetPage();
  };

  const openBuyerDetail = async (nickname: string) => {
    setLoadingDetail(true);
    const res = await api.get<BuyerDetail>(`/contacts/buyers/${encodeURIComponent(nickname)}`);
    if (res.success && res.data) setSelectedBuyer(res.data);
    setLoadingDetail(false);
  };

  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleDateString() : '--';
  // "Total gastado" es dinero: sin la llave se muestra "—".
  const fmtMoney = (v: number) => (canMoney ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

  return (
    <>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: t('contacts.buyers'), value: stats.total_buyers.toLocaleString(), color: 'var(--color-primary)' },
            { label: t('contacts.newBuyers'), value: stats.new_buyers_30d.toLocaleString(), color: 'var(--color-success)' },
            { label: t('contacts.topBuyer'), value: stats.top_buyer?.nickname || '--', color: 'var(--color-primary)' },
            { label: t('contacts.avgOrders'), value: String(stats.avg_orders_per_buyer), color: 'var(--color-text-muted)' },
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <div className="text-xl font-bold truncate" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
        <input
          type="text"
          placeholder={t('contacts.searchPlaceholder')}
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
        />
      </div>

      {/* Table */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="animate-pulse p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-[var(--color-border-light)] rounded" />)}
          </div>
        ) : buyers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
            <Users size={48} className="opacity-20 mb-3" />
            <p>{t('contacts.noContacts')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('contacts.nickname')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('contacts.totalOrders')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('contacts.totalSpent')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('contacts.firstOrder')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('contacts.lastOrder')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('contacts.sites')}</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map(b => (
                  <tr
                    key={b.nickname}
                    className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] cursor-pointer"
                    onClick={() => openBuyerDetail(b.nickname)}
                  >
                    <td className="px-3 py-2.5">
                      <span className="font-semibold text-sm text-[var(--color-text)]">{b.nickname}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm font-mono text-[var(--color-text)]">{b.total_orders}</td>
                    <td className="px-3 py-2.5 text-right text-sm font-mono text-[var(--color-text)]">{fmtMoney(b.total_spent)}</td>
                    <td className="px-3 py-2.5 text-sm text-[var(--color-text-muted)]">{fmtDate(b.first_order)}</td>
                    <td className="px-3 py-2.5 text-sm text-[var(--color-text-muted)]">{fmtDate(b.last_order)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {(b.sites || []).map(site => (
                          <span key={site} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                            {SITE_FLAGS[site] || ''} {site}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-[var(--color-border)]">
          <Pagination
            page={pag.page}
            totalPages={pag.totalPages}
            total={pag.total}
            perPage={pag.perPage}
            onPageChange={pag.setPage}
            onPerPageChange={pag.setPerPage}
          />
        </div>
      </div>

      {/* Buyer Detail Panel */}
      {(selectedBuyer || loadingDetail) && (
        <BuyerDetailPanel
          buyer={selectedBuyer}
          loading={loadingDetail}
          onClose={() => setSelectedBuyer(null)}
        />
      )}
    </>
  );
}

function BuyerDetailPanel({
  buyer,
  loading,
  onClose,
}: {
  buyer: BuyerDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleDateString() : '--';
  const fmtMoney = (v: number) => (canMoney ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-[var(--color-surface)] shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-[var(--color-text)]">{t('contacts.buyerDetail')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]">
            <X size={20} />
          </button>
        </div>

        {loading || !buyer ? (
          <div className="p-5 animate-pulse space-y-4">
            <div className="h-16 bg-[var(--color-border-light)] rounded" />
            <div className="h-32 bg-[var(--color-border-light)] rounded" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Header */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[var(--color-primary-light)] flex items-center justify-center">
                <Users size={24} className="text-[var(--color-primary)]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[var(--color-text)]">{buyer.nickname}</h3>
                <div className="flex items-center gap-2 mt-1 text-sm text-[var(--color-text-muted)]">
                  <span>{buyer.total_orders} {t('contacts.orders')}</span>
                  <span>-</span>
                  <span>{fmtMoney(buyer.total_spent)}</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="px-4 py-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)] text-center">
                <div className="text-lg font-bold text-[var(--color-primary)]">{buyer.total_orders}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{t('contacts.totalOrders')}</div>
              </div>
              <div className="px-4 py-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)] text-center">
                <div className="text-lg font-bold text-[var(--color-success)]">{fmtMoney(buyer.total_spent)}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{t('contacts.totalSpent')}</div>
              </div>
            </div>

            {/* Dates & Sites */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] font-semibold mb-2">Info</h4>
              <div className="bg-[var(--color-bg)] rounded-lg border border-[var(--color-border-light)] p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-muted)]">{t('contacts.firstOrder')}</span>
                  <span className="text-[var(--color-text)]">{fmtDate(buyer.first_order)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-muted)]">{t('contacts.lastOrder')}</span>
                  <span className="text-[var(--color-text)]">{fmtDate(buyer.last_order)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-text-muted)]">{t('contacts.sites')}</span>
                  <div className="flex gap-1">
                    {(buyer.sites || []).map(site => (
                      <span key={site} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-[var(--color-surface)] border border-[var(--color-border-light)]">
                        {SITE_FLAGS[site] || ''} {site}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] font-semibold mb-2">{t('contacts.recentOrders')}</h4>
              <div className="bg-[var(--color-bg)] rounded-lg border border-[var(--color-border-light)] p-3">
                {buyer.recent_orders.length === 0 ? (
                  <div className="flex flex-col items-center py-4 text-[var(--color-text-muted)]">
                    <ShoppingCart size={24} className="opacity-20 mb-1" />
                    <span className="text-xs">{t('common.noData')}</span>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {buyer.recent_orders.map(o => (
                      <div key={o.ml_order_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--color-surface-hover)] text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-[var(--color-primary)]">#{o.ml_order_id}</span>
                          <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400'}`}>
                            {o.status}
                          </span>
                          {o.context_site && (
                            <span className="text-[var(--color-text-muted)]">
                              {SITE_FLAGS[o.context_site] || ''} {o.context_site}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono">{fmtMoney(o.paid_amount)}</span>
                          <span className="text-[var(--color-text-muted)]">{fmtDate(o.date_created)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function SuppliersTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-8 text-center">
      <Users size={48} className="mx-auto opacity-20 mb-4 text-[var(--color-text-muted)]" />
      <p className="text-[var(--color-text-muted)] mb-4">{t('contacts.suppliers')}</p>
      <button
        onClick={() => navigate('/suppliers')}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90"
      >
        <ExternalLink size={16} />
        {t('contacts.goToSuppliers')}
      </button>
    </div>
  );
}
